import { Parser } from 'json2csv'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFont from 'pdfmake/build/vfs_fonts'

class CSVExport {

  constructor ({
    definition,
    results,
    summary,
    translations
  }) {
    this.definition = definition
    this.results = results
    this.summary = summary
    this.translations = translations
  }

  export () {
    // results
    const data = Object.keys(this.results)
      .filter(key => !!Number(key))
      .map(idx => {
        const value = this.results[idx].value
        const note = this.results[idx].note
        return {
          num: idx,
          label: CSVExport.getLabel(this.definition, Number(idx)),
          value: Array.isArray(value) ? value.length : value,
          note
        }
      })

    // summary
    if (this.summary.required) {
      const statusText = this.summary.status === 'OK'
        ? this.translations['checklists.ok']
        : this.summary.status === 'OK_REMARKS'
        ? this.translations['checklists.ok-with-remarks']
        : this.translations['checklists.not-ok']

      data.push({ num: '', label: '', value: '', note: ''})
      data.push({ num: '', label: this.translations['checklists.summary'], value: statusText, note: this.summary.remarks })
    }

    // parse to CSV
    const parser = new Parser({ withBOM: true, fields: ['num', 'label', 'value', 'note'] })
    const csv = parser.parse(data)

    return csv
  }

  // helpers

  static getLabel (obj, idx) {
    if (obj.idx === idx) {
      return obj.label
    }
    if (obj.items) {
      for (let item of obj.items) {
        let label = CSVExport.getLabel(item, idx)
        if (label) {
          return label
        }
      }
    }
    return null
  }

}

class PDFExport {

  constructor ({
    definition,
    results,
    images,
    asset,
    checklist,
    intervention,
    translations,
    summary
  }) {
    pdfMake.vfs = pdfFont.pdfMake.vfs

    this.definition = definition
    this.results = results
    this.images = images
    this.asset = asset
    this.checklist = checklist
    this.intervention = intervention
    this.translations = translations
    this.summary = summary
  }

  export () {
    const content = []

    // title
    content.push({
      text: `${this.checklist.name} [${this.checklist.code}]`,
      bold: true,
      fontSize: 14,
      margin: [0, 0, 0, 0]
    })

    // divider
    content.push(PDFExport.divider())

    // metadata + asset attributes
    const date = (this.intervention.workEndAt === null ? new Date() : new Date(this.intervention.workEndAt))
        .toISOString()
        .replace('T', ' ')
        .replace('Z', '')

    const users = this.intervention.usersInInterventions.reduce((users, record) => {
      const { user } = record
      if (user.firstname && user.lastname) {
        users.push(`${user.firstname} ${user.lastname}`)
      } else {
        users.push(user.email)
      }
      return users
    }, [])

    content.push({
      columns: [
        // metadata
        {
          text: [
            {
              text: [
                { text: `${this.translations['assets.entity-name']}: `, bold: true },
                this.asset.name,
                '\n'
              ],
              margin: [0, 0, 0, 15]
            },
            {
              text: [
                { text: `${this.translations['team']}: `, bold: true },
                users.join(', '),
                '\n'
              ],
              margin: [0, 0, 0, 15]
            },
            {
              text: [
                { text: `${this.translations['date']}: `, bold: true },
                date,
                '\n'
              ],
              margin: [0, 0, 0, 15]
            }
          ]
        },
        // attributes
        {
          text: [
            {
              text: [
                { text: `${this.translations['asset-attributes.entity-name']}: `, bold: true },
                '\n'
              ]
            },
            ...PDFExport.attributes(this.asset.assetAttributesInAssets)
          ]
        }
      ],
      alignment: 'left',
      columnGap: 25
    })

    if (this.summary.required) {
      // divider
      content.push(PDFExport.divider())

      // summary
      const statusText = this.summary.status === 'OK'
        ? this.translations['checklists.ok']
        : this.summary.status === 'OK_REMARKS'
        ? this.translations['checklists.ok-with-remarks']
        : this.translations['checklists.not-ok']

      content.push({
        text: [
          { text: this.translations['checklists.summary'], bold: true },
          '\n',
          this.translations['checklists.summary-status'] + ': ',
          { text: statusText, italics: true },
          '\n',
          this.translations['checklists.summary-remarks'] + ': ',
          { text: this.summary.remarks, italics: true }
        ]
      })
    }

    // divider
    content.push(PDFExport.divider())

    // results
    content.push({
      layout: 'lightHorizontalLines',
      margin: [0, 15, 0, 0],
      table: {
        headerRows: 1,
        widths: [100, '*', '*'],
        body: [
          [this.translations['value'], this.translations['label'] + '/' + this.translations['description'], this.translations['note']],
          ...PDFExport.flattenTable(this.definition, this.results, this.images)
        ]
      }
    })

    const doc = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      defaultStyle: { fontSize: 10 },
      content
    }

    return pdfMake.createPdf(doc)
  }

  // helpers

  static divider () {
    return {
      canvas: [ { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 } ],
      margin: [0, 10, 0, 10]
    }
  }

  static attributes (items) {
    return items.map(item => ({
      text: [
        '- ',
        { text: item.assetAttribute.name, bold: true },
        ' ',
        item.value,
        '\n'
      ]
    }))
  }

  static note (result) {
    const text = result ? result.note : undefined
    return { text: text || '', fontSize: 9 }
  }

  static image (source) {
    return {
      image: source['medium']
    }
  }

  static value (result, type) {
    const val = result ? result.value : undefined
    const T = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAAh0lEQVR42mNgYGD4TyKGEEQCDA3bgHgLEr8PiC/g0yAOxC+BOAuIXYH4KxBr49MAAl5QhS+AOI+Qk2DgHBC/A2J2YjSkAPEbIL4OxD2ENKgC8RcgDgBiPSD+DvULVg0sQHwKiGchKSgC4me4NMQA8U0g5kZSwAjEu3Fp4ABiISwBwIxNA9EYAJ8oQCNM8wHbAAAAAElFTkSuQmCC'
    const F = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAAGUlEQVR42mNgYGD4TyKGEESCUQ1DXQPRGAAY9CvVk0f66wAAAABJRU5ErkJggg=='
    if (/switch/.test(type)) {
      if (val === undefined || val === null) {
        return {
          columns: [
            { image: F },
            { text: 'yes' },
            { image: F },
            { text: 'no' }
          ]
        }
      }
      if (val === true) {
        return {
          columns: [
            { image: T },
            { text: 'yes' },
            { image: F },
            { text: 'no' }
          ]
        }
      }
      if (val === false) {
        return {
          columns: [
            { image: F },
            { text: 'yes' },
            { image: T },
            { text: 'no' }
          ]
        }
      }
    } else {
      return val || ''
    }
  }

  static flattenTable (obj, results, images) {
    let out = []

    if (/section/.test(obj.type) && obj.lvl > 0) {
      out.push([
        { text: '', fillColor: '#F5F5F5' },
        {
          text: [
            { text: obj.label ? obj.label + '\n' : '', bold: true },
            { text: obj.description || '', fontSize: 9 }
          ],
          fillColor: '#F5F5F5'
        },
        { text: '', fillColor: '#F5F5F5' }
      ])
    }

    if (/info/.test(obj.type)) {
      out.push([
        { text: '' },
        {
          text: [
            { text: obj.label ? obj.label + '\n' : '', bold: true },
            { text: obj.description || '', fontSize: 9 }
          ]
        },
        { text: '' }
      ])
    }

    if (/image/.test(obj.type)) {
      out.push([
        { text: '' },
        {
          text: [
            { text: obj.label ? obj.label + '\n' : '', bold: true },
            { text: obj.description || '', fontSize: 9 }
          ]
        },
        PDFExport.note(results[obj.idx])
      ])
      if (obj.items && obj.items.length) {
        let pics = []
        for (let id of obj.items) {
          let src = images[id]
          if (src) pics.push(PDFExport.image(src))
        }
        if (pics.length) {
          out.push([{
            stack: pics,
            colSpan: 3
          }])
        }
      }
    }

    if (/switch|number|string/.test(obj.type) && results[obj.idx]) {
      out.push([
        PDFExport.value(results[obj.idx], obj.type),
        {
          text: [
            { text: obj.label ? obj.label + '\n' : '', bold: true },
            { text: obj.description || '', fontSize: 9 }
          ]
        },
        PDFExport.note(results[obj.idx])
      ])
    }

    if (/file/.test(obj.type) && results[obj.idx]) {
      out.push([
        { text: '' },
        {
          text: [
            { text: obj.label ? obj.label + '\n' : '', bold: true },
            { text: obj.description || '', fontSize: 9 }
          ]
        },
        PDFExport.note(results[obj.idx])
      ])
      if (results[obj.idx] && results[obj.idx].value) {
        let pics = []
        for (let id of results[obj.idx].value) {
          let src = images[id]
          if (src) pics.push(PDFExport.image(src))
        }
        if (pics.length) {
          out.push([{
            stack: pics,
            colSpan: 3
          }])
        }
      }
    }

    if (obj.items && !/image|file/.test(obj.type)) {
      for (let item of obj.items) {
        let result = PDFExport.flattenTable(item, results, images)
        if (result) {
          out = out.concat(result)
        }
      }
    }

    return out
  }

}

export {
  CSVExport,
  PDFExport
}
